import React, { useEffect, useRef } from "react";
import { Helmet } from "react-helmet-async";
import { WorldDesk } from "../components/WorldDesk";

const INK = [
  { size: "l", r: "-3deg", flash: "heart", skin: "skin-heart" },
  { size: "l", r: "-2deg", flash: "quote", skin: "skin-quote" },
  { size: "m", r: "-1deg", flash: "kitten", skin: "skin-sphynx" },
  { size: "l", r: "0deg", flash: "butterflies", skin: "skin-butterflies" },
  { size: "w", r: "1deg", flash: "anime", skin: "skin-anime" },
  { size: "m", r: "2deg", flash: "bird", skin: null },
  { size: "m", r: "3deg", flash: "puppy", skin: "skin-figure" },
  { size: "m", r: "4deg", flash: "armflowers", skin: "skin-armflowers" },
  { size: "m", r: "-4deg", flash: "lily", skin: "skin-lily" },
  { size: "w", r: "-3deg", flash: "faces", skin: "skin-faces" },
  { size: "s", r: "-2deg", flash: "branch", skin: null },
  { size: "s", r: "-1deg", flash: "floral", skin: null },
  { size: "l", r: "0deg", flash: "dragonfly", skin: "skin-dragonfly" },
  { size: "m", r: "1deg", flash: "fox", skin: "skin-fox" },
  { size: "m", r: "2deg", flash: "rose", skin: "skin-rose" },
  { size: "s", r: "3deg", flash: "sun", skin: "skin-sun" },
  { size: "m", r: "4deg", flash: "bluefly", skin: "skin-bluefly" },
  { size: "s", r: "-4deg", flash: "filigree", skin: "skin-filigree" },
];

const PASSES = [
  { company: "Chaos Global", role: "Lead Designer", gate: "Gurgaon", dates: "Apr 2025 — Present", note: "Pitch ideation, creative direction, client comms, production. Built the studio Creative Tracker on Vercel.", status: "boarding", edge: "var(--lime)" },
  { company: "Thea Pâtisserie", role: "Head Designer", gate: "Bengaluru", dates: "Jan 2021 — Jun 2025", note: "Identity, packaging, menus, print and digital marketing — a cohesive premium brand system, concept to production.", status: "landed", edge: "var(--blue)" },
  { company: "Beyond Hue", role: "Social Media Manager & Designer", gate: "Surat", dates: "Jun 2024 — May 2025", note: "Platform-specific statics and short-form for lifestyle and hospitality accounts; content, scheduling, analytics.", status: "landed", edge: "var(--blue)" },
  { company: "StudioD", role: "Graphic Designer (Contract) · Intern", gate: "—", dates: "May — Aug 2023", note: "Branding and client projects in a fast-paced studio — creative direction, execution, client-facing design.", status: "landed", edge: "var(--blue)" },
  { company: "Freelance, since 18", role: "Graphic Designer — first flight", gate: "Remote", dates: "Mar 2020 — Present", note: "Took my first paid brief at 18 and never landed. Founders, small businesses and studios — identity, stationery, print, surface graphics.", status: "still flying", edge: "var(--ink)" },
];

const SKILLS = [
  { t: "Brand identity" }, { t: "Visual systems", o: true }, { t: "Packaging" },
  { t: "Campaign art direction", o: true }, { t: "Editorial & decks" }, { t: "Typography", o: true },
  { t: "Layout" }, { t: "Illustration", o: true }, { t: "Production-ready artwork" }, { t: "AI image + film direction", o: true },
];

const TOOLS = ["Illustrator", "Photoshop", "InDesign", "Firefly", "Figma", "Canva", "Procreate", "Midjourney", "ChatGPT", "Claude", "Higgsfield", "Vercel", "GitHub", "Final Cut Pro", "Logic Pro"];

const SCHOOLS = [
  ["AND Academy", "Post Graduate Diploma, Graphic Design", "PGD"],
  ["FLAME University", "BBA Communications — Major Advertising & Branding, Minor Graphic Design", "BBA"],
  ["California Institute of the Arts", "Certificate Course, Graphic Design", "CERT"],
  ["Sri Sri Academy", "ISC, Class XII — 96.5 percentile", "2021"],
];

function Skills() {
  const ref = useRef(null);
  useEffect(() => {
    const SK = ref.current;
    if (!SK) return;
    SK.querySelectorAll(".sk").forEach((w) => {
      const t = w.textContent;
      w.dataset.t = t;
      w.innerHTML = t.split(" ").map((wd) => '<span class="wd">' + [...wd].map((c) => '<span class="ch">' + (c === "&" ? "&amp;" : c) + "</span>").join("") + "</span>").join(" ");
    });
    const io = new IntersectionObserver((es) => es.forEach((en) => {
      if (!en.isIntersecting) return;
      SK.querySelectorAll(".ch").forEach((c) => { c.style.transitionDelay = (Math.random() * 0.7).toFixed(2) + "s"; });
      SK.classList.add("in");
      io.disconnect();
    }), { threshold: 0.3 });
    io.observe(SK);
    const G = "ABCDEFGHIJKLMNOPQRSTUVWXYZ#*/+";
    const hovers = [];
    SK.querySelectorAll(".sk").forEach((w) => {
      const onEnter = () => {
        const t = w.dataset.t.replace(/ /g, "");
        const cs = w.querySelectorAll(".ch");
        let f = 0;
        const iv = setInterval(() => {
          cs.forEach((c, i) => { c.textContent = i < f / 2 ? t[i] : G[(Math.random() * G.length) | 0]; });
          if (++f > t.length * 2) { clearInterval(iv); cs.forEach((c, i) => { c.textContent = t[i] === " " ? " " : t[i]; }); }
        }, 28);
      };
      w.addEventListener("mouseenter", onEnter);
      hovers.push(() => w.removeEventListener("mouseenter", onEnter));
    });
    return () => { io.disconnect(); hovers.forEach((h) => h()); };
  }, []);
  return (
    <div className="skills" ref={ref} data-testid="about-skills">
      {SKILLS.map((s) => <span key={s.t} className={`sk${s.o ? " o" : ""}`}>{s.t}</span>)}
    </div>
  );
}

export default function About() {
  return (
    <div data-testid="about-page">
      <Helmet><title>About — Poonam Baid | vdesigns.eps</title></Helmet>

      {/* HERO */}
      <div className="wrap" style={{ paddingTop: 90 }}>
        <div className="disp" style={{ fontSize: "clamp(3.4rem,15vw,14rem)", color: "var(--blue)" }}>Behind</div>
        <div className="disp about-file" style={{ fontSize: "clamp(3.4rem,15vw,14rem)", color: "var(--blue)", textAlign: "right" }}>the file</div>
      </div>

      {/* OPEN THE DESK — my world (replaces the old collage/stickers) */}
      <WorldDesk ticketHref="/#reel" />

      {/* (01) short version */}
      <section className="wrap about-sec">
        <div className="sh"><span className="mono">(01) The short version</span></div>
        <h2 style={{ fontSize: "clamp(1.5rem,3.4vw,2.4rem)", lineHeight: 1.12, fontWeight: 500, width: "100%" }}>
          Five years of turning <span className="serif" style={{ color: "var(--blue)" }}>open-ended briefs</span> into scalable visual systems — for founders, studios and marketing teams who need the idea and the artwork to <span className="hl">hold up everywhere.</span>
        </h2>
        <div className="about-cols">
          <p>I lead pitch ideation and translate briefs into clear creative directions across identity, packaging, campaigns, presentations, print and digital. I own client communication, coordinate production and keep the work consistent all the way to delivery.</p>
          <p>Outside the brief I illustrate, make posters about things that matter, and build tools — like the studio's Creative Tracker, designed and shipped on Vercel to keep every project visible.</p>
        </div>
      </section>

      {/* INK — flash sheet of tattoos */}
      <section className="wrap ink" id="ink" data-testid="ink-section">
        <div className="sh"><span className="mono">Ink — a flash sheet of my tattoos</span><span className="mono">18 of my 70+ · click one to see it on skin</span></div>
        <div className="ink-sheet">
          <div className="ink-title"><b>FLASH</b><span>all of these live on me</span></div>
          <div className="ink-grid">
            {INK.map((t, i) => (
              <button
                key={t.flash}
                className={`ink-c ink-${t.size}${t.skin ? "" : " noflip"}`}
                style={{ "--r": t.r }}
                aria-label={`Tattoo ${String(i + 1).padStart(2, "0")}`}
                data-testid={`ink-card-${i + 1}`}
                onClick={(e) => { if (t.skin) e.currentTarget.classList.toggle("on"); }}
              >
                <div className="f"><img src={`/assets/tattoos/${t.flash}.webp`} alt={`Tattoo flash ${i + 1}`} /><i>No. {String(i + 1).padStart(2, "0")}</i></div>
                {t.skin && <div className="b"><img src={`/assets/tattoos/${t.skin}.jpg`} alt="The tattoo on skin" loading="lazy" /><span>on skin</span></div>}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* (02) experience boarding passes */}
      <section className="wrap about-sec">
        <div className="sh"><span className="mono">(02) Experience — boarding passes</span><span className="mono">2020 → now</span></div>
        {PASSES.map((p) => (
          <div key={p.company} className="bpass" data-testid={`pass-${p.company}`}>
            <div className="bpass-edge" style={{ background: p.edge }} />
            <div className="bpass-co"><span className="mono bpass-k">Company</span><div className="disp" style={{ fontSize: "1.6rem" }}>{p.company}</div></div>
            <div><span className="mono bpass-k">Role</span><p style={{ fontSize: 13 }}>{p.role}</p></div>
            <div><span className="mono bpass-k">Gate · Dates</span><p style={{ fontSize: 13 }}>{p.gate}<br />{p.dates}</p></div>
            <div><span className="mono bpass-k">On board</span><p style={{ fontSize: 13 }}>{p.note}</p></div>
            <div style={{ paddingRight: 18 }}><span className="mono" style={{ fontSize: 9 }}>Status: {p.status}</span><div className="bc" style={{ marginTop: 8 }} /></div>
          </div>
        ))}
      </section>

      {/* (03) skills & tools on dark */}
      <section className="theme-ink">
        <div className="wrap about-sec">
          <div className="sh" style={{ borderColor: "#fff" }}><span className="mono">(03) Skills &amp; tools</span></div>
          <div className="skills-grid">
            <Skills />
            <div>
              <span className="mono" style={{ color: "#888", display: "block", marginBottom: 14 }}>Tools I work in</span>
              <div className="flex flex-wrap">
                {TOOLS.map((t) => <span key={t} className="tool">{t}</span>)}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* schooled in ads & type */}
      <section className="wrap about-sec">
        <div className="schooled-grid">
          <div>
            <div className="disp" style={{ fontSize: "clamp(2.4rem,5vw,3.4rem)" }}>Schooled<br />in <span className="serif" style={{ color: "var(--blue)" }}>ads &amp; type.</span></div>
            <svg className="school-ill" viewBox="0 0 300 150" fill="none" stroke="var(--blue)" strokeWidth="2" strokeLinecap="round">
              <rect x="8" y="22" width="150" height="112" rx="3" fill="#fff" />
              <text x="22" y="98" fontFamily="Instrument Serif" fontStyle="italic" fontSize="78" fill="var(--blue)" stroke="none">Aa</text>
              <path d="M22 112h110M22 120h80" stroke="#cfc6ae" />
              <path d="M196 18l64 64-20 20-64-64z" fill="var(--lime)" stroke="var(--ink)" />
              <path d="M176 38l-26 46 46-26" fill="#fff" stroke="var(--ink)" />
              <circle cx="170" cy="64" r="4" fill="var(--ink)" stroke="none" />
              <path d="M150 84c-22 18-40 22-56 18" strokeDasharray="4 6" />
              <text x="196" y="138" fontFamily="Reenie Beanie" fontSize="22" fill="var(--grey)" stroke="none">ads &amp; type, since 18</text>
            </svg>
          </div>
          <div style={{ fontSize: 14 }}>
            {SCHOOLS.map(([name, desc, tag]) => (
              <div key={name} className="school-row">
                <b>{name}</b><span>{desc}</span><span className="mono">{tag}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CV band */}
      <section className="theme-blue">
        <div className="wrap" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 24, flexWrap: "wrap" }}>
          <div className="disp" style={{ fontSize: "clamp(3rem,7vw,5rem)", color: "#fff" }}>Get the CV</div>
          <a className="btn btn--lime" href="/cv/Poonam-Baid-CV-2026.pdf" download data-testid="about-cv">Download ↓</a>
        </div>
      </section>
    </div>
  );
}
